import axios from 'axios';
class FeatureFlagsApi {
    constructor(baseURL = '', tokenKey = 'token', onAuthError) {
        this.tokenKey = tokenKey;
        this.onAuthError = onAuthError;
        this.apiClient = axios.create({
            baseURL,
            timeout: 10000,
        });
        // Add auth token interceptor
        this.apiClient.interceptors.request.use((config) => {
            const token = localStorage.getItem(this.tokenKey);
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        });
        // Handle errors
        this.apiClient.interceptors.response.use((response) => response, (error) => {
            if (error.response?.status === 401) {
                // Token expired or invalid
                localStorage.removeItem(this.tokenKey);
                // Use callback if provided, otherwise fallback to hard redirect
                if (this.onAuthError) {
                    this.onAuthError();
                }
            }
            return Promise.reject(error);
        });
    }
    /**
     * Fetch feature flags for a pharmacy
     * @param pharmacyId - The pharmacy ID to fetch flags for
     * @returns Object with flag names as keys and boolean values
     */
    async getFeatureFlags(pharmacyId) {
        try {
            const response = await this.apiClient.get('feature-flags', {
                params: { pharmacyId },
            });
            return response.data;
        }
        catch (error) {
            console.error('Failed to fetch feature flags:', error);
            throw error;
        }
    }
    /**
     * Toggle a feature flag for a pharmacy (admin only)
     * @param flagName - The flag name to toggle
     * @param enabled - Whether to enable or disable the flag
     * @param pharmacyId - The pharmacy ID to toggle for
     */
    async toggleFeatureFlag(flagName, enabled, pharmacyId) {
        try {
            await this.apiClient.patch(`feature-flags/${flagName}`, {
                enabled,
                pharmacyId,
            });
        }
        catch (error) {
            console.error('Failed to toggle feature flag:', error);
            throw error;
        }
    }
    /**
     * Reset a feature flag to default for a pharmacy (admin only)
     * @param flagName - The flag name to reset
     * @param pharmacyId - The pharmacy ID to reset for
     */
    async resetFeatureFlag(flagName, pharmacyId) {
        try {
            await this.apiClient.delete(`feature-flags/${flagName}/reset`, {
                data: { pharmacyId },
            });
        }
        catch (error) {
            console.error('Failed to reset feature flag:', error);
            throw error;
        }
    }
    /**
     * Get admin view of all flags (admin only)
     */
    async getAdminView() {
        try {
            const response = await this.apiClient.get('feature-flags/admin');
            return response.data;
        }
        catch (error) {
            console.error('Failed to fetch admin view:', error);
            throw error;
        }
    }
    /**
     * Get all feature flag definitions (admin only)
     * @returns Array of feature flag definitions with default values
     */
    async getFeatureFlagDefinitions() {
        try {
            const response = await this.apiClient.get('feature-flag-definitions');
            return response.data;
        }
        catch (error) {
            console.error('Failed to fetch feature flag definitions:', error);
            throw error;
        }
    }
    /**
     * Update a feature flag definition's active state (admin only)
     * This controls whether the flag is active system-wide
     * @param flagName - The flag name to update
     * @param isActive - Whether the flag should be active system-wide
     */
    async updateFeatureFlagDefinition(flagName, isActive) {
        try {
            await this.apiClient.patch(`feature-flag-definitions/${flagName}`, {
                isActive,
            });
        }
        catch (error) {
            console.error('Failed to update feature flag definition:', error);
            throw error;
        }
    }
}
// Export singleton instance
let apiInstance = null;
export const getFeatureFlagsApi = (baseURL, tokenKey, onAuthError) => {
    if (!apiInstance) {
        apiInstance = new FeatureFlagsApi(baseURL, tokenKey, onAuthError);
    }
    return apiInstance;
};
export default FeatureFlagsApi;
